/**
 * AppLayout - Main application shell for all authenticated pages.
 *
 * Provides the persistent navigation chrome that wraps every protected route.
 * Uses a three-component navigation system:
 *	 - Sidebar (desktop only) — fixed left column, 240px wide
 *	 - TopBar (both viewports) — sticky top bar with search, icons, avatar
 *	 - BottomTabBar (mobile only) — fixed bottom tab navigation
 *
 * Desktop layout:
 * ┌─────────────────────────────────────────────┐
 * │  Sidebar  │		  TopBar				 │
 * │  (fixed)  │─────────────────────────────────│
 * │		   │  SyncStatusBar (if visible)	 │
 * │   240px   │─────────────────────────────────│
 * │		   │								 │
 * │		   │	   Main Content				 │
 * │		   │	   (scrollable)				 │
 * │		   │								 │
 * │		   │		  Footer				 │
 * └───────────┴─────────────────────────────────┘
 *
 * Mobile layout:
 * ┌─────────────────────────┐
 * │		TopBar			 │
 * ├─────────────────────────┤
 * │  SyncStatusBar (if vis) │
 * ├─────────────────────────┤
 * │						 │
 * │	 Main Content		 │
 * │	 (scrollable)		 │
 * │						 │
 * ├─────────────────────────┤
 * │	 BottomTabBar		 │
 * └─────────────────────────┘
 *
 * Key behaviors:
 *	 - Desktop: content area has margin-left of 240px to clear the fixed Sidebar
 *	 - Mobile: no sidebar; bottom tab bar visible; content has bottom padding
 *	   to prevent the tab bar from covering the last bit of scrollable content
 *	 - Footer is hidden on mobile (bottom tabs occupy that space)
 *	 - SyncStatusBar (offline/sync banner) renders above main content in both layouts
 *	 - Main content area scrolls independently within the viewport
 *
 * @example
 * <AppLayout>
 *	   <DashboardPage />
 * </AppLayout>
 */
import React from 'react';
import { useLocation } from 'react-router-dom';
import { Sidebar } from '../Sidebar';
import { TopBar } from '../TopBar';
import { BottomTabBar } from '../BottomTabBar';
import { SyncStatusBar } from '../SyncStatusBar';
import { Footer } from '../Footer';
import { ImpersonationBanner } from '../ImpersonationBanner';
import { useAuth } from '../../../hooks/useAuth';

const AppLayout: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className }) => {
	// Track route changes to re-trigger the mobile page-enter animation
	const location = useLocation();
	// Pull impersonation state to conditionally render the banner and add top padding
	const { isImpersonating } = useAuth();
	return (
		/* min-h-[100dvh] rather than min-h-screen: on mobile Safari/Chrome
		   100vh is the LARGE viewport (address bar expanded), so a screen-tall
		   shell always overflows by the height of the browser chrome and the
		   bottom of the page hides behind it. dvh tracks the bar collapsing as
		   the user scrolls. min-h-screen stays as the pre-dvh fallback. */
		<div className={`app-layout min-h-screen min-h-[100dvh] bg-[#f5f5f5] ${className ?? ''}`} data-ref="organism-app-layout">

			{/* ===== Impersonation Banner (conditional) ===== */}
			{/* Renders a fixed amber banner at the very top of the viewport when
				a Super Admin is impersonating another user. Sits above everything
				including the TopBar via z-50. */}
			{isImpersonating && <ImpersonationBanner />}

			{/* ===== Sidebar (desktop only) ===== */}
			{/* Fixed left column — hidden on mobile, visible on tablet+.
				The Sidebar component manages its own visibility breakpoints. */}
			<Sidebar />

			{/* ===== Main content wrapper ===== */}
			{/* On desktop: offset left by 240px to clear the fixed Sidebar.
				On mobile: full width with no left offset. */}
			<div className={`app-layout__content tablet:ml-[240px] flex flex-col min-h-screen min-h-[100dvh] ${isImpersonating ? 'pt-[36px]' : ''}`}>

				{/* ===== TopBar (sticky, both viewports) ===== */}
				{/* Sticky at the top of the scrollable area. Shows search + icons
					on desktop and brand + compact icons on mobile. */}
				<TopBar />

				{/* ===== SyncStatusBar (conditional offline banner) ===== */}
				{/* Renders above main content when the user is offline, has pending
					sync items, or a sync operation just completed. Hidden when
					online with nothing to sync. */}
				<SyncStatusBar />

				{/* ===== Main content area ===== */}
				{/* flex-1 ensures the content area expands to push the Footer to
					the bottom even on short pages. On mobile, extra bottom padding
					(pb-80px) prevents the BottomTabBar from covering content.
					The pb accounts for the 64px tab bar height plus safe area. */}
				{/* key={location.pathname} forces React to remount on route change,
					re-triggering the page-transition CSS animation on mobile */}
				<main key={location.pathname} className="app-layout__body flex-1 w-full mx-auto max-w-7xl px-16px tablet:px-24px desktop:px-32px py-24px pb-80px tablet:pb-24px page-transition">
					{children}
				</main>

				{/* ===== Footer (desktop only) ===== */}
				{/* Hidden on mobile because the BottomTabBar occupies the bottom
					of the viewport. Visible on tablet+ below the main content. */}
				<div className="hidden tablet:block">
					<Footer />
				</div>
			</div>

			{/* ===== BottomTabBar (mobile only) ===== */}
			{/* Fixed at the bottom of the viewport — visible on mobile, hidden on
				tablet+. The BottomTabBar component manages its own visibility
				breakpoints internally. */}
			<BottomTabBar />
		</div>
	);
};

export default AppLayout;
